// src/analytics/acquisition.ts
//
// First-touch acquisition capture. Records the campaign / referrer that first
// brought a visitor and keeps it in localStorage, so it survives across sessions
// on this device and can be attached to their account at signup. This is what
// turns "clicks" into "accounts by channel" for ad-spend decisions.

import { detectInAppBrowser, IN_APP_NAMES } from '../utils/inAppBrowser';

const KEY = 'chaptery.acquisition';

export interface Acquisition {
  source?: string;    // utm_source  (e.g. author-nation, reddit, google, meta)
  medium?: string;    // utm_medium  (e.g. qr, cpc, social)
  campaign?: string;  // utm_campaign
  term?: string;      // utm_term
  content?: string;   // utm_content
  referrer?: string;  // external referrer host, when no UTM was present
  landingPath?: string;
  firstSeen: string;  // ISO timestamp
  /**
   * The app whose in-built browser this arrived in, when we can name it —
   * "Instagram", "Facebook", and so on.
   *
   * This is why the field exists: our second customer came from Instagram and
   * produced no acquisition record whatsoever, because its webview supplies no
   * referrer and the link carried no UTM. The app already identifies Instagram
   * at signup for a different reason, so the signal was sitting unused. (#730)
   */
  inApp?: string;
  /**
   * The ad platform whose click ID the landing URL carried: Google Ads adds gclid (or
   * gbraid/wbraid), Meta fbclid, Reddit rdt_cid, with or without UTM tags. Without this,
   * an ad run untagged looked exactly like an organic visit. Only the platform is kept,
   * never the ID: an ID can be joined to the platform's data about the person. (#825)
   *
   * Meta adds fbclid to every outbound link opened from Facebook or Instagram (posts, bio
   * links, stories and ads alike), so "meta" means a click from there, not necessarily a
   * paid one. gclid, gbraid, wbraid and rdt_cid come from ads only. Some browsers strip
   * these parameters (Safari's private browsing and link protection, Firefox, Brave), so
   * this undercounts.
   */
  clickSource?: ClickSource;
  /** No campaign, no click ID and no external referrer — typed in, bookmarked, or stripped. */
  direct?: boolean;
}

export type ClickSource = 'google' | 'meta' | 'reddit';

const CLICK_SOURCES: ClickSource[] = ['google', 'meta', 'reddit'];

// In this order: fbclid first, because Facebook and Instagram add it to whatever link was
// clicked there, so with another platform's ID beside it (a Google ad's link, shared in a
// post) the click still happened on Facebook or Instagram.
const CLICK_IDS: Array<[string, ClickSource]> = [
  ['fbclid', 'meta'],
  ['gclid', 'google'], ['gbraid', 'google'], ['wbraid', 'google'],
  ['rdt_cid', 'reddit'],
];

/**
 * Which platform's click ID is in these query parameters, if any. Never the ID. A visit
 * that left an in-app browser through our sign-up link names it in from_click instead,
 * from a closed list, as from_app names the app (#937).
 */
function clickSourceOf(p: URLSearchParams): ClickSource | undefined {
  const fromClick = p.get('from_click') as ClickSource | null;
  return CLICK_IDS.find(([param]) => !!p.get(param))?.[1]
    ?? (fromClick && CLICK_SOURCES.includes(fromClick) ? fromClick : undefined);
}

function isExternalReferrer(ref: string): boolean {
  if (!ref) return false;
  try { return new URL(ref).host !== window.location.host; } catch { return false; }
}

/**
 * Store first-touch attribution. First touch wins — later visits don't overwrite it.
 *
 * Every first touch is recorded, including plain direct ones. This used to skip
 * them "so the data stays meaningful", which was reasonable until it wasn't: an
 * absent row is indistinguishable from never having asked, so the denominator was
 * wrong and a real customer who arrived from Instagram counted as nothing at all.
 * A row saying "no campaign, no referrer, arrived in Instagram's browser, landed on
 * /compare" is far more useful than silence. (#730)
 */
export function captureAcquisition(): void {
  if (typeof window === 'undefined') return;
  try {
    if (localStorage.getItem(KEY)) return; // already captured — first touch wins
  } catch {
    return; // storage unavailable
  }

  const p = new URLSearchParams(window.location.search);
  const source = p.get('utm_source') || undefined;
  const medium = p.get('utm_medium') || undefined;
  const campaign = p.get('utm_campaign') || undefined;
  const ref = document.referrer || '';
  const external = isExternalReferrer(ref);
  const clickSource = clickSourceOf(p);

  // In-app browsers strip the referrer, so this is often the ONLY signal that a
  // visit came from a social channel at all. A visit that left one through our
  // "Sign up with Google or Apple" link names the app in from_app (#937). Only known
  // names are accepted, so a URL cannot write arbitrary text into the record.
  const fromApp = p.get('from_app');
  const inApp = detectInAppBrowser()?.app
    || (fromApp && IN_APP_NAMES.includes(fromApp) ? fromApp : undefined);

  const acq: Acquisition = {
    source,
    medium,
    campaign,
    term: p.get('utm_term') || undefined,
    content: p.get('utm_content') || undefined,
    referrer: external ? ref : undefined,
    landingPath: window.location.pathname,
    firstSeen: new Date().toISOString(),
    inApp,
    clickSource,
    direct: !source && !medium && !campaign && !clickSource && !external,
  };

  try {
    localStorage.setItem(KEY, JSON.stringify(acq));
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function getAcquisition(): Acquisition | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Acquisition) : null;
  } catch {
    return null;
  }
}

const UTM_FIELDS: Array<[keyof Acquisition, string]> = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['content', 'utm_content'],
  ['term', 'utm_term'],
];

/**
 * The link that takes a visitor out of an in-app browser (#937).
 *
 * The other browser starts with empty storage, so without this the account would
 * be credited to nothing: the single biggest reason half our trials had no source.
 * Carries the stored campaign, names the app the visit started in, and names the
 * platform whose click ID brought it (the name only, never the ID; #825).
 */
export function firstTouchUrl(url: string, app: string): string {
  const out = new URL(url);
  const acq = getAcquisition();
  for (const [field, param] of UTM_FIELDS) {
    const value = acq?.[field];
    if (typeof value === 'string' && value && !out.searchParams.has(param)) {
      out.searchParams.set(param, value);
    }
  }
  if (acq?.clickSource && CLICK_SOURCES.includes(acq.clickSource)) out.searchParams.set('from_click', acq.clickSource);
  out.searchParams.set('from_app', app);
  return out.toString();
}
